import { useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { toast } from 'src/components/ui/toast';
import ConfirmDialog from 'src/components/common/ConfirmDialog';

// Revoking an override works the same on both override panels: pick the row,
// confirm what reverting it means, then take it out of service now. The row
// being edited can be the row being revoked, so the caller gets a hook to
// drop that edit. See docs/procurement/lead-time-overrides.md.

interface RevokeHandlers {
  onSuccess: () => void;
  onError: (e: Error) => void;
}

export interface OverrideRevokeState<T> {
  /** The row awaiting confirmation; null while the dialog is closed. */
  target: T | null;
  /**
   * True when that row's window has not started, so revoking cancels it
   * rather than stopping something. The list that owns the row supplies this
   * when it requests the revoke: it has already judged every row against one
   * clock, and reading the clock again here would be an impure render.
   */
  targetScheduled: boolean;
  request: (o: T, scheduled?: boolean) => void;
  cancel: () => void;
  confirm: () => void;
  pending: boolean;
}

export function useOverrideRevoke<T>({
  revoke,
  pending,
  successMessage,
  onRevoked,
}: {
  /** Fire the revoke for this row; the hook owns the target and the toasts. */
  revoke: (o: T, handlers: RevokeHandlers) => void;
  pending: boolean;
  successMessage: string;
  /** The revoke landed: a chance to drop an edit of the row that just went. */
  onRevoked?: (o: T) => void;
}): OverrideRevokeState<T> {
  const gt = useGT();
  // One piece of state, so the row and its scheduled flag cannot disagree.
  const [pending_, setPending] = useState<{
    row: T;
    scheduled: boolean;
  } | null>(null);
  const target = pending_?.row ?? null;

  function confirm() {
    if (!target) return;
    const revoking = target;
    revoke(revoking, {
      onSuccess: () => {
        toast.success(successMessage);
        onRevoked?.(revoking);
        setPending(null);
      },
      onError: (e: Error) =>
        toast.error(
          gt('Failed to revoke: {msg}', {
            context: 'procurement override error toast',
            msg: e.message,
          }),
        ),
    });
  }

  return {
    target,
    targetScheduled: pending_?.scheduled ?? false,
    request: (o: T, scheduled = false) => setPending({ row: o, scheduled }),
    cancel: () => setPending(null),
    confirm,
    pending,
  };
}

export function OverrideRevokeDialog<T>({
  revoke,
  title,
  message,
  wide = false,
  children,
}: {
  revoke: OverrideRevokeState<T>;
  title: string;
  /** What reverting this override means, in the panel's own words. */
  message: string | undefined;
  /** True when the body carries a preview table rather than just the message. */
  wide?: boolean;
  children?: ReactNode;
}) {
  const gt = useGT();
  return (
    <ConfirmDialog
      open={revoke.target !== null}
      onClose={revoke.cancel}
      onConfirm={revoke.confirm}
      closeOnConfirm={false}
      loading={revoke.pending}
      dangerous
      wide={wide}
      title={title}
      message={message}
      confirmText={gt('Revoke', {
        context: 'procurement override revoke button',
      })}
    >
      {children}
    </ConfirmDialog>
  );
}
